import {
  currencyNumber,
  currencyString,
  LocationDTO,
  stringPercentToNumber,
} from '@sales-center/common'
import {
  DiscountType,
  ILeadOppProduct,
} from '@user/store/leadOppProducts/typings'
import { SERVICE_TYPE } from '@user/store/serviceProducts/typings'

// change currency types from number to string
export interface ILeadOppProductFormModel
  extends Omit<
    ILeadOppProduct,
    | 'initialPrice'
    | 'initialDiscountAmount'
    | 'recurringPrice'
    | 'recurringDiscountAmount'
    | 'leadId'
  > {
  initialPrice: string
  initialDiscountAmount: string
  recurringPrice: string
  recurringDiscountAmount: string
  serviceLocationId: string
}

export const initialModel: ILeadOppProduct = {
  id: -1,

  externalId: '',
  name: '',
  description: '',

  frequencyId: '',
  annualOccurrences: 0,

  quantity: 1,
  upSell: false,

  initialPrice: 0,
  initialDiscountAmount: 0,
  initialDiscountType: 'dollar',

  recurringPrice: 0,
  recurringDiscountAmount: 0,
  recurringDiscountType: 'dollar',

  taxRate: 0,
  chargeTax: false,
  subtractOne: false,

  totalFirst: 0,
  totalSubsequent: 0,

  serviceType: SERVICE_TYPE.SERVICE,
  serviceLocationId: '',

  appointmentEntityId: null,
  appointmentEntityType: null,
  appointmentScheduledBy: null,
  appointmentScheduledTime: null,
  estimateEntityId: null,
  estimateEntityType: null,
  estimateScheduledBy: null,
  estimateScheduledTime: null,

  sortOrder: 0,

  canadianTaxRate: false,
  gstHstLabel: null,
  gstHstRate: 0,
  pstQstLabel: null,
  pstQstRate: 0,
}

export function normalize(
  model: ILeadOppProductFormModel,
  entityId?: number,
): ILeadOppProduct {
  const {
    initialPrice,
    initialDiscountAmount,
    recurringPrice,
    recurringDiscountAmount,
    ...obj
  } = model

  return {
    ...obj,
    initialPrice: currencyNumber(initialPrice),
    initialDiscountAmount:
      currencyNumber(initialPrice) > 0
        ? obj.initialDiscountType === 'percentage'
          ? stringPercentToNumber(initialDiscountAmount)
          : currencyNumber(initialDiscountAmount)
        : 0,
    recurringPrice: currencyNumber(recurringPrice),
    recurringDiscountAmount:
      currencyNumber(recurringPrice) > 0
        ? obj.recurringDiscountType === 'percentage'
          ? stringPercentToNumber(recurringDiscountAmount)
          : currencyNumber(recurringDiscountAmount)
        : 0,
    entityId,
  }
}

export function denormalize(
  model: ILeadOppProduct,
  locations: LocationDTO[],
  localMode: boolean,
): ILeadOppProductFormModel {
  const {
    initialPrice,
    initialDiscountAmount,
    recurringPrice,
    recurringDiscountAmount,
    serviceLocationId,
    ...obj
  } = model

  return {
    ...obj,
    initialPrice: initialPrice === 0 ? '' : currencyString(initialPrice),
    initialDiscountAmount:
      initialDiscountAmount === 0
        ? ''
        : obj.initialDiscountType === 'percentage'
          ? initialDiscountAmount * 100 + '%'
          : currencyString(initialDiscountAmount),
    recurringPrice: recurringPrice === 0 ? '' : currencyString(recurringPrice),
    recurringDiscountAmount:
      recurringDiscountAmount === 0
        ? ''
        : obj.recurringDiscountType === 'percentage'
          ? recurringDiscountAmount * 100 + '%'
          : currencyString(recurringDiscountAmount),
    serviceLocationId:
      (serviceLocationId ? String(serviceLocationId) : '') ||
      (locations.length === 1
        ? localMode
          ? locations[0].externalLinkId!
          : locations[0].id.toString()
        : ''),
  }
}

export function validate(
  model: ILeadOppProductFormModel,
): Record<string, string> {
  const errors: Record<string, string> = {}
  const service = normalize(model)

  if (service.externalId == '') {
    errors.externalId = 'Required'
  }
  if (service.serviceLocationId == '') {
    errors.serviceLocationId = 'Required'
    if (service.externalId == '') errors.externalId = 'Select Service Location'
  }
  if (service.quantity <= 0) errors.quantity = 'Must be > 0'
  if (model.serviceType == SERVICE_TYPE.SERVICE && service.frequencyId == '')
    errors.frequencyId = 'Required'

  if (service.initialDiscountType === 'dollar') {
    if (
      service.initialPrice >= 0 &&
      service.initialDiscountAmount > service.initialPrice
    )
      errors.initialDiscountAmount = `Must be <= ${service.initialPrice}`
    else if (service.initialDiscountAmount < 0)
      errors.initialDiscountAmount = 'Must be >= 0'
  } else {
    if (service.initialDiscountAmount > 1) {
      errors.initialDiscountAmount = 'Must be < 100%'
    } else if (service.initialDiscountAmount < 0) {
      errors.initialDiscountAmount = 'Must be >= 0'
    }
  }

  // recurring
  if (service.recurringDiscountType === 'dollar') {
    if (
      service.recurringPrice >= 0 &&
      service.recurringDiscountAmount > service.recurringPrice
    )
      errors.recurringDiscountAmount = `Must be <= ${service.recurringPrice}`
    else if (service.recurringDiscountAmount < 0)
      errors.recurringDiscountAmount = 'Must be >= 0'
  } else {
    if (service.recurringDiscountAmount > 1) {
      errors.recurringDiscountAmount = 'Must be < 100%'
    } else if (service.recurringDiscountAmount < 0) {
      errors.recurringDiscountAmount = 'Must be >= 0'
    }
  }

  return errors
}

export function subTotal(
  price: string,
  discount: string,
  type: DiscountType,
): number {
  const actualPrice = currencyNumber(price)
  if (type === 'percentage') {
    const actualDiscount = stringPercentToNumber(discount) * actualPrice
    return actualPrice - actualDiscount
  }
  return actualPrice > 0 ? actualPrice - currencyNumber(discount) : actualPrice
}

export function tax(subTotal: number, taxRate: number): number {
  return subTotal > 0 ? subTotal * taxRate : 0
}

export function total(
  subTotal: number,
  tax: number,
  chargeTax: boolean,
): number {
  return chargeTax ? subTotal + tax : subTotal
}

export function serviceTotal(
  initial: number,
  recurring: number,
  annualOccurrences: number,
  quantity: number,
): number {
  const qty = quantity > 0 ? quantity : 1
  return (initial + recurring * annualOccurrences) * qty
}
